import { useVirtualizer } from '@tanstack/react-virtual'
import React, { useCallback, useRef } from 'react'

import ListItem from './ListItem'
import ListNormalItem from './ListNormalItem'
import * as SC from './styles'
import type { ListItemData, NormalListProps } from './types'

export function List<T extends ListItemData>({
    data = [],
    style,
    rowKey,
    hoveredIndex,
    disableSelect = false,
    description,
    itemStyle,
    estimateSize = 38,
    itemRender,
    onItemClick,
    disabled
}: NormalListProps<T>) {
    const listRef = useRef<HTMLDivElement>(null)

    const rowVirtualizer = useVirtualizer({
        count: data?.length ?? 0,
        getScrollElement: () => listRef.current,
        estimateSize: () => estimateSize,
        getItemKey: (index: number) => data[index].value,
        overscan: 10
    })

    const handleItemClick = useCallback(
        (itemData: string | number) => {
            if (!disableSelect) {
                onItemClick?.(`${itemData}`)
            }
        },
        [disableSelect, onItemClick]
    )

    return (
        <>
            {!!description && <SC.Description>{description}</SC.Description>}
            <SC.ListScroller style={style} ref={listRef}>
                <SC.ListWrapper
                    style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        position: 'relative'
                    }}
                >
                    {rowVirtualizer.getVirtualItems().map(virtualRow => {
                        const { index, start, size } = virtualRow
                        const item = data[index]
                        if (itemRender) {
                            return (
                                <ListNormalItem
                                    style={{
                                        ...itemStyle,
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        height: `${size}px`,
                                        transform: `translateY(${start}px)`
                                    }}
                                    key={rowKey ? String(item[rowKey]) : item.value}
                                    hovered={hoveredIndex === index}
                                    onClick={() => {
                                        handleItemClick(item.value)
                                    }}
                                >
                                    {itemRender(item, index)}
                                </ListNormalItem>
                            )
                        }
                        return (
                            <ListItem
                                key={rowKey ? String(item[rowKey]) : item.value}
                                index={index}
                                dataKey={rowKey ? String(item[rowKey]) : item.value}
                                itemStyle={{
                                    ...itemStyle,
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    height: `${size}px`,
                                    transform: `translateY(${start}px)`
                                }}
                                hovered={hoveredIndex === index}
                                data={item}
                                onClick={() => {
                                    handleItemClick(item.value)
                                }}
                            />
                        )
                    })}
                </SC.ListWrapper>
            </SC.ListScroller>
        </>
    )
}
